import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IMAGES } from '@/assets/images';
import { TOTAL_SLIDES } from '@/lib/index';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Slide components ──────────────────────────────────────────────

function SlideCover() {
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.COVER_BG})` }}
      />
      {/* Dark overlay with red tint gradient */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, rgba(10,10,10,0.52) 0%, rgba(40,4,4,0.38) 50%, rgba(10,10,10,0.56) 100%)'
      }} />
      {/* Red side accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-5xl">
        {/* Year badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="mb-6 px-6 py-2 rounded-full border text-sm font-semibold tracking-widest uppercase"
          style={{
            borderColor: 'var(--primary)',
            color: 'var(--primary)',
            background: 'rgba(227,30,36,0.1)',
            fontFamily: 'JetBrains Mono, monospace',
          }}
        >
          Barcelona · Des de 1964
        </motion.div>

        {/* Giant number */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35, duration: 0.8 }}
          className="leading-none font-black mb-4"
          style={{
            fontFamily: 'Oswald, sans-serif',
            fontSize: 'clamp(6rem, 22vw, 20rem)',
            color: 'var(--primary)',
            textShadow: '0 0 80px rgba(227,30,36,0.4)',
            lineHeight: 0.9,
          }}
        >
          60
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.7 }}
        >
          <p className="uppercase tracking-[0.3em] text-white/60 text-sm font-medium mb-2"
            style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Anys · Model Català del
          </p>
          <h1
            className="font-black uppercase text-white"
            style={{
              fontFamily: 'Oswald, sans-serif',
              fontSize: 'clamp(1.8rem, 5vw, 4rem)',
              letterSpacing: '0.08em',
            }}
          >
            CB Grup Barna
          </h1>
        </motion.div>

        {/* Bottom decorative line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-8 h-px w-48"
          style={{ background: 'linear-gradient(90deg, transparent, var(--primary), transparent)' }}
        />

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-6 text-white/50 text-base font-light tracking-wide max-w-lg"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Impulsant el desenvolupament esportiu i humà al Poblenou i el Clot
        </motion.p>
      </div>
    </div>
  );
}

function SlideFamilies() {
  return (
    <div className="relative w-full h-full flex overflow-hidden">
      {/* Right panel: player photo GB.147 — chica + chico, encuadrado completo */}
      <div
        className="absolute right-0 top-0 bottom-0 w-[55%] bg-cover bg-top"
        style={{ backgroundImage: `url(${IMAGES.GB_147})` }}
      />
      {/* Gradient que funde la foto con el fondo oscuro */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(90deg, rgba(10,10,10,0.82) 38%, rgba(10,10,10,0.35) 60%, rgba(10,10,10,0.0) 100%)'
      }} />
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-center px-16 md:px-24 max-w-2xl">
        <motion.p
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="uppercase tracking-widest text-xs font-semibold mb-6"
          style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
        >
          · Comunitat
        </motion.p>

        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25, duration: 0.7 }}
          className="font-black leading-none mb-6"
          style={{
            fontFamily: 'Oswald, sans-serif',
            fontSize: 'clamp(4rem, 14vw, 12rem)',
            color: 'var(--primary)',
            textShadow: '0 0 60px rgba(227,30,36,0.35)',
          }}
        >
          +450
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="font-bold uppercase text-white mb-4"
          style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(1.4rem, 3.5vw, 2.6rem)', letterSpacing: '0.1em' }}
        >
          Famílies
        </motion.h2>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="h-px w-24 mb-6"
          style={{ background: 'var(--primary)', transformOrigin: 'left' }}
        />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          className="text-white/70 text-base md:text-lg font-light leading-relaxed max-w-md mb-8"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          +450 famílies, +40 equips de formació masculins i femenins, impulsant el desenvolupament esportiu i humà dels nens i nenes del Poblenou i el Clot.
        </motion.p>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.6 }}
          className="flex gap-8"
        >
          {[{ num: '+40', label: 'Equips' }, { num: '2', label: 'Barris' }, { num: '60', label: 'Anys' }].map((s) => (
            <div key={s.label} className="flex flex-col">
              <span
                className="font-black leading-none"
                style={{
                  fontFamily: 'Oswald, sans-serif',
                  fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                  color: 'var(--primary)',
                }}
              >{s.num}</span>
              <span className="text-white/50 text-xs uppercase tracking-widest mt-1"
                style={{ fontFamily: 'Montserrat, sans-serif' }}>{s.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

function SlideEquality() {
  // Fotos del cos tècnic — grid 2×2 a la dreta
  const coachPhotos = [
    { src: IMAGES.ENT_42, label: 'Entrenadoras' },
    { src: IMAGES.ENT_65, label: 'Staff tècnic' },
    { src: IMAGES.ENT_36, label: 'Equip formatiu' },
    { src: IMAGES.ENT_47, label: 'Cos tècnic' },
  ];

  return (
    <div className="relative w-full h-full flex overflow-hidden">
      {/* Fondo: Torre Glòries — monument Sant Martí */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.TORRE_GLORIES})` }}
        />
        <div className="absolute inset-0" style={{
          background: 'rgba(8,8,8,0.65)'
        }} />
      </div>
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      {/* Layout: text left | photo grid right */}
      <div className="relative z-10 w-full h-full flex items-center px-12 md:px-16 gap-10">

        {/* ── LEFT: text content ── */}
        <div className="flex flex-col justify-center flex-shrink-0 w-[44%]">
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="uppercase tracking-widest text-xs font-semibold mb-5"
            style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
          >
            · Igualtat &amp; Lideratge
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-black uppercase text-white mb-5"
            style={{
              fontFamily: 'Oswald, sans-serif',
              fontSize: 'clamp(1.5rem, 3.5vw, 3rem)',
              letterSpacing: '0.08em',
              lineHeight: 1.1,
            }}
          >
            Igualtat i<br />Lideratge Femení
          </motion.h2>

          {/* Big stat */}
          <motion.div
            initial={{ opacity: 0, scale: 0.75 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="flex items-baseline gap-2 mb-2"
          >
            <span
              className="font-black leading-none"
              style={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: 'clamp(3.5rem, 10vw, 8rem)',
                color: 'var(--primary)',
                textShadow: '0 0 50px rgba(227,30,36,0.45)',
              }}
            >65.5%</span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-white/60 text-sm mb-5"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            del staff tècnic <span className="text-white/90 font-semibold">són dones</span>
          </motion.p>

          {/* 38 badge */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="flex items-center gap-4 mb-5 px-4 py-3 rounded-xl"
            style={{
              background: 'rgba(227,30,36,0.08)',
              border: '1px solid rgba(227,30,36,0.3)',
            }}
          >
            <span
              className="font-black"
              style={{ fontFamily: 'Oswald, sans-serif', fontSize: '2.6rem', color: 'var(--primary)' }}
            >38</span>
            <span className="text-white/70 text-sm leading-snug" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              entrenadores<br /><span className="text-white/45 text-xs">actives al club</span>
            </span>
          </motion.div>

          {/* Bullets */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.72, duration: 0.5 }}
            className="space-y-2 mb-5"
          >
            {[
              'Pla propi de formació i mentoria interna',
              'Model estructural, no estètic',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--primary)' }} />
                <p className="text-white/55 text-xs leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item}</p>
              </div>
            ))}
          </motion.div>

          {/* Quote */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="border-l-2 pl-3 py-1"
            style={{ borderColor: 'var(--primary)' }}
          >
            <p
              className="text-white font-semibold italic text-sm leading-snug"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              "No fem campanyes.<br />Creem referents."
            </p>
          </motion.div>
        </div>

        {/* ── RIGHT: 2×2 photo grid ── */}
        <div className="flex-1 h-full flex items-center py-10">
          <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-3">
            {coachPhotos.map((photo, i) => (
              <motion.div
                key={photo.label}
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.25 + i * 0.12, duration: 0.6 }}
                className="relative overflow-hidden rounded-xl"
                style={{ border: '1px solid rgba(227,30,36,0.2)' }}
              >
                <div
                  className="absolute inset-0 bg-cover bg-top"
                  style={{ backgroundImage: `url(${photo.src})` }}
                />
                {/* subtle bottom gradient for label */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-10"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)' }}
                />
                {/* Top accent rim */}
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'var(--primary)', opacity: 0.6 }} />
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

function SlideDiversity() {
  return (
    <div className="relative w-full h-full flex overflow-hidden">
      {/* Fons fosc pur */}
      <div className="absolute inset-0" style={{ background: 'oklch(0.10 0.012 240)' }} />
      {/* Jugador Màgics — panel dret explícit */}
      <div
        className="absolute right-0 top-0 bottom-0 w-[52%] bg-cover"
        style={{ backgroundImage: `url(${IMAGES.MAGICS_005})`, backgroundPosition: 'center top' }}
      />
      {/* Gradient de fusió text/foto */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(90deg, rgba(10,10,10,1) 34%, rgba(10,10,10,0.55) 52%, rgba(10,10,10,0.0) 68%)'
      }} />
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-center px-16 md:px-24 max-w-2xl">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="uppercase tracking-widest text-xs font-semibold mb-5"
          style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
        >
          · Diversitat &amp; Inclusió
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-black uppercase text-white mb-4"
          style={{
            fontFamily: 'Oswald, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.8rem)',
            letterSpacing: '0.06em',
          }}
        >
          Els Màgics
        </motion.h2>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="h-px w-20 mb-6"
          style={{ background: 'var(--primary)', transformOrigin: 'left' }}
        />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-white/70 text-base leading-relaxed max-w-md mb-8"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Oferim espais de pràctica esportiva a joves amb diversitat funcional. Equips inclusius que van més enllà del bàsquet: aprenen valors, fan amics i formen part d'una comunitat real.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.5 }}
          className="space-y-3 mb-8"
        >
          {[
            'Integració social real, dins i fora de la pista',
            'Amb i sense diversitat funcional, junts',
            'Bàsquet adaptat com a eina de vida',
          ].map((item, i) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + i * 0.12, duration: 0.4 }}
              className="flex items-start gap-3"
            >
              <div
                className="mt-2 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(227,30,36,0.15)', border: '1px solid rgba(227,30,36,0.5)' }}
              >
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--primary)' }} />
              </div>
              <p className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {item}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="border-l-2 pl-4 py-1"
          style={{ borderColor: 'var(--primary)' }}
        >
          <p
            className="text-white font-semibold italic"
            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'clamp(0.85rem, 1.8vw, 1.1rem)' }}
          >
            "L'esport no entén de barreres.<br />Els Màgics, tampoc."
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function SlideCommunity() {
  return (
    <div className="relative w-full h-full flex overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.COMMUNITY_BG})` }}
        />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(90deg, rgba(8,8,8,0.65) 45%, rgba(8,8,8,0.12) 100%)'
        }} />
      </div>
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      <div className="relative z-10 flex flex-col justify-center px-16 md:px-24 max-w-2xl">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="uppercase tracking-widest text-xs font-semibold mb-6"
          style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
        >
          · Comunitat &amp; Educació
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="font-black uppercase text-white mb-8"
          style={{
            fontFamily: 'Oswald, sans-serif',
            fontSize: 'clamp(1.8rem, 4.5vw, 3.6rem)',
            letterSpacing: '0.08em',
          }}
        >
          Comunitat<br />i Educació
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="space-y-4 mb-10"
        >
          {[
            'Els clubs esportius són escoles de vida',
            'Complement informal al sistema educatiu',
            'Aprenentatge en valors, infants i famílies',
          ].map((item, i) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.45 + i * 0.12, duration: 0.5 }}
              className="flex items-start gap-3"
            >
              <div
                className="mt-2 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(227,30,36,0.15)', border: '1px solid rgba(227,30,36,0.4)' }}
              >
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--primary)' }} />
              </div>
              <p className="text-white/75 text-base leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {item}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="relative p-6 rounded-2xl"
          style={{
            background: 'rgba(227,30,36,0.08)',
            border: '1px solid rgba(227,30,36,0.3)',
          }}
        >
          <div className="absolute -top-px left-8 right-8 h-px" style={{
            background: 'linear-gradient(90deg, transparent, var(--primary), transparent)'
          }} />
          <p
            className="text-white font-bold italic leading-snug"
            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'clamp(1rem, 2vw, 1.4rem)' }}
          >
            "Cada nen necessita un equip.<br />Cada equip necessita una comunitat."
          </p>
          <p className="mt-3 text-white/40 text-xs uppercase tracking-widest" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            — C.B. Grup Barna
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function SlideProtection() {
  const mesures = [
    'Designació d\'un responsable de protecció del menor',
    'Protocols d\'actuació davant situacions de risc',
    'Formació del staff i entrenadors en protecció',
    'Compromís ètic i de conducta per a tot el personal',
  ];
  const canals = [
    'Canal confidencial per comunicar incidències',
    'Contacte directe amb el responsable de protecció',
    'Comunicació oberta amb famílies, jugadors i entrenadors',
  ];
  return (
    <div className="relative w-full h-full flex overflow-hidden">
      {/* Fons fosc amb accent suau */}
      <div className="absolute inset-0" style={{ background: 'oklch(0.11 0.014 240)' }} />
      {/* Cercle decoratiu */}
      <div className="absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full opacity-[0.04]"
        style={{ background: 'var(--primary)' }} />
      <div className="absolute -left-24 -bottom-24 w-[360px] h-[360px] rounded-full opacity-[0.03]"
        style={{ background: 'var(--primary)' }} />
      {/* Línia accent top */}
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--primary)' }} />
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      <div className="relative z-10 w-full h-full flex items-center px-12 md:px-16 gap-10">

        {/* ── LEFT ── */}
        <div className="flex flex-col justify-center flex-shrink-0 w-[40%]">
          <motion.p
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="uppercase tracking-widest text-xs font-semibold mb-4"
            style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
          >
            · Seguretat &amp; Compromís
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-black uppercase text-white mb-3 leading-tight"
            style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(1.6rem, 3.5vw, 3rem)', letterSpacing: '0.07em' }}
          >
            Protecció<br />del Menor<br />en l'Esport
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            transition={{ delay: 0.32, duration: 0.5 }}
            className="h-px w-16 mb-4"
            style={{ background: 'var(--primary)', transformOrigin: 'left' }}
          />
          {/* LOPIVI badge */}
          <motion.div
            initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex items-center gap-3 px-4 py-3 rounded-xl mb-5 w-fit"
            style={{ background: 'rgba(227,30,36,0.10)', border: '1px solid rgba(227,30,36,0.4)' }}
          >
            <span style={{ fontSize: '1.3rem' }}>🛡️</span>
            <div>
              <p className="text-white font-bold text-sm leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>Homologat LOPIVI</p>
              <p className="text-white/45 text-xs mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Llei de Protecció del Menor en l'Esport</p>
            </div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.52, duration: 0.5 }}
            className="text-white/60 text-sm leading-relaxed max-w-xs"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            El club ha implementat un sistema de protecció integral per garantir un entorn segur per a tots els infants i joves.
          </motion.p>
          {/* Objectiu box */}
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.92, duration: 0.5 }}
            className="mt-5 border-l-2 pl-3 py-1"
            style={{ borderColor: 'var(--primary)' }}
          >
            <p className="text-white/40 text-xs uppercase tracking-widest mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Objectiu</p>
            <p className="text-white/75 text-xs leading-relaxed italic" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              "Crear un entorn esportiu segur, educatiu i respectuós, on els infants puguin créixer esportivament i personalment."
            </p>
          </motion.div>
        </div>

        {/* ── RIGHT: logo + mesures + canals ── */}
        <div className="flex-1 flex flex-row justify-center gap-4 items-stretch py-10">
          {/* Poster oficial LOPIVI */}
          <motion.div
            initial={{ opacity: 0, scale: 0.93 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="flex-shrink-0 w-36 rounded-2xl overflow-hidden self-center"
            style={{ border: '1px solid rgba(227,30,36,0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
          >
            <img src={IMAGES.LOGO_PROTECCIO} alt="Protocol Marc Protecció Infants" className="w-full h-full object-cover" />
          </motion.div>
          {/* Mesures + Canals */}
          <div className="flex-1 flex flex-col justify-center gap-3">
          {/* Mesures */}
          <motion.div
            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="p-5 rounded-2xl"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <p className="text-xs uppercase tracking-widest font-semibold mb-4"
              style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>
              Mesures implementades
            </p>
            <div className="space-y-2.5">
              {mesures.map((m, i) => (
                <motion.div key={m}
                  initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                  className="flex items-start gap-3"
                >
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--primary)' }} />
                  <p className="text-white/70 text-sm leading-snug" style={{ fontFamily: 'Montserrat, sans-serif' }}>{m}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Canals */}
          <motion.div
            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.55, duration: 0.6 }}
            className="p-5 rounded-2xl"
            style={{ background: 'rgba(227,30,36,0.05)', border: '1px solid rgba(227,30,36,0.18)' }}
          >
            <p className="text-xs uppercase tracking-widest font-semibold mb-4"
              style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>
              Canals de comunicació
            </p>
            <div className="space-y-2.5">
              {canals.map((c, i) => (
                <motion.div key={c}
                  initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.65 + i * 0.1, duration: 0.4 }}
                  className="flex items-start gap-3"
                >
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--primary)', opacity: 0.7 }} />
                  <p className="text-white/65 text-sm leading-snug" style={{ fontFamily: 'Montserrat, sans-serif' }}>{c}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          </div>{/* end mesures+canals */}
        </div>{/* end right flex-row */}

      </div>
    </div>
  );
}

function SlideNeighborhood() {
  const venues = [
    'La Nau del Clot',
    'Escola Provençals',
    'La Farigola del Clot',
    'La Rambleta del Clot',
    'Escola Casas',
    'Claror Marítim',
  ];

  const partners = [
    {
      logo: IMAGES.LOGO_EIX_CLOT,
      name: 'Eix del Clot',
      desc: 'Acord de col·laboració tancat',
    },
    {
      logo: IMAGES.LOGO_SANT_MARTI,
      name: 'Districte Sant Martí',
      desc: 'Acord de col·laboració tancat',
    },
    {
      logo: IMAGES.LOGO_FEDE_CLOT,
      name: "Federació d'Entitats Clot-Camp de l'Arpa",
      desc: 'Acord de col·laboració tancat',
    },
    {
      logo: IMAGES.LOGO_EIX_SANT_MARTI,
      name: 'Eix Sant Martí',
      desc: 'Acord de col·laboració tancat',
    },
  ];

  return (
    <div className="relative w-full h-full flex overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.MERCAT_CLOT_NEW})` }}
        />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(90deg, rgba(8,8,8,0.75) 50%, rgba(8,8,8,0.25) 100%)'
        }} />
      </div>
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      <div className="relative z-10 flex w-full h-full items-center px-12 md:px-16 gap-10">

        {/* ── LEFT: text + venues ── */}
        <div className="flex flex-col justify-center flex-shrink-0 w-[48%]">
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="uppercase tracking-widest text-xs font-semibold mb-5"
            style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
          >
            · Implicats al Barri
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-black uppercase text-white mb-3"
            style={{
              fontFamily: 'Oswald, sans-serif',
              fontSize: 'clamp(1.8rem, 4vw, 3.4rem)',
              letterSpacing: '0.08em',
            }}
          >
            Implicats<br />al Barri
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.32, duration: 0.5 }}
            className="text-white/60 text-sm mb-6 max-w-sm"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Hem tancat acords amb els principals agents del territori i estem presents a 6 escoles del barri.
          </motion.p>

          {/* Venues grid */}
          <div className="grid grid-cols-2 gap-2 max-w-sm">
            {venues.map((venue, i) => (
              <motion.div
                key={venue}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38 + i * 0.08, duration: 0.4 }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
              >
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--primary)' }} />
                <span className="text-white/75 text-xs font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {venue}
                </span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.5 }}
            className="mt-4"
          >
            <span className="text-xs uppercase tracking-widest font-semibold"
              style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>
              Pistes d'entrenament &amp; partits — CB Grup Barna
            </span>
          </motion.div>
        </div>

        {/* ── RIGHT: partner logos ── */}
        <div className="flex-1 flex flex-col justify-center gap-3">
          {/* Section label */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex items-center gap-3 mb-1"
          >
            <div className="h-px flex-1" style={{ background: 'rgba(227,30,36,0.4)' }} />
            <span className="text-xs uppercase tracking-widest font-semibold whitespace-nowrap"
              style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>
              Acords signats
            </span>
            <div className="h-px flex-1" style={{ background: 'rgba(227,30,36,0.4)' }} />
          </motion.div>

          {partners.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + i * 0.18, duration: 0.6 }}
              className="flex items-center gap-5 p-4 rounded-2xl"
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {/* Logo */}
              <div
                className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden flex items-center justify-center p-2"
                style={{ background: 'white' }}
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  className="w-full h-full object-contain"
                />
              </div>
              {/* Text */}
              <div className="flex flex-col">
                <span
                  className="text-white font-bold text-sm leading-tight mb-1"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {p.name}
                </span>
                {/* "Acord tancat" badge */}
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit"
                  style={{
                    background: 'rgba(227,30,36,0.15)',
                    border: '1px solid rgba(227,30,36,0.4)',
                    color: 'var(--primary)',
                    fontFamily: 'JetBrains Mono, monospace',
                  }}
                >
                  <span style={{ fontSize: '0.55rem' }}>●</span> Acord tancat
                </span>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </div>
  );
}

function SlideStreet() {
  return (
    <div className="relative w-full h-full flex overflow-hidden">
      {/* Bg */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.STREET_BG})` }}
        />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(90deg, rgba(8,8,8,0.62) 40%, rgba(8,8,8,0.15) 100%)'
        }} />
      </div>
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      <div className="relative z-10 flex flex-col justify-center px-16 md:px-24 max-w-3xl">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="uppercase tracking-widest text-xs font-semibold mb-6"
          style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
        >
          · Esport al Carrer
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="font-black uppercase text-white mb-6"
          style={{
            fontFamily: 'Oswald, sans-serif',
            fontSize: 'clamp(1.8rem, 4.5vw, 3.8rem)',
            letterSpacing: '0.08em',
          }}
        >
          Esport al Carrer<br />
          <span style={{ color: 'var(--primary)' }}>3×3 Barna</span>
        </motion.h2>

        {/* Main stat */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          className="flex items-baseline gap-2 mb-6"
        >
          <span
            className="font-black leading-none"
            style={{
              fontFamily: 'Oswald, sans-serif',
              fontSize: 'clamp(3.5rem, 12vw, 9rem)',
              color: 'var(--primary)',
              textShadow: '0 0 50px rgba(227,30,36,0.4)',
            }}
          >
            +110
          </span>
          <span className="text-white/60 text-xl font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            equips<br />participants
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.5 }}
          className="space-y-3 mb-8"
        >
          {[
            '2 jornades amb pista al CC Westfield Glòries i al Barna',
            'Activació comunitària intergeneracional',
            'Comerç local, famílies i barri',
            'Model replicable a altres municipis',
          ].map((item, i) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + i * 0.1, duration: 0.4 }}
              className="flex items-start gap-3"
            >
              <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--primary)' }} />
              <p className="text-white/65 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="border-l-2 pl-4 py-1"
          style={{ borderColor: 'var(--primary)' }}
        >
          <p
            className="text-white font-semibold italic"
            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'clamp(0.9rem, 2vw, 1.15rem)' }}
          >
            "L'esport és convivència,<br />salut i orgull col·lectiu."
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function SlideAward() {
  return (
    <div className="relative w-full h-full flex overflow-hidden">
      {/* Fons: foto Saló de Cent */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.PREMI_DONA_1})` }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(90deg, rgba(8,8,8,0.90) 0%, rgba(8,8,8,0.75) 45%, rgba(8,8,8,0.20) 100%)'
        }} />
      </div>
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      {/* Layout principal */}
      <div className="relative z-10 w-full h-full flex items-center px-12 md:px-16 gap-10">

        {/* ── ESQUERRA ── */}
        <div className="flex flex-col justify-center w-[48%]">
          {/* Badge oval top */}
          <motion.div
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 w-fit"
            style={{ background: 'rgba(227,30,36,0.15)', border: '1px solid rgba(227,30,36,0.5)' }}
          >
            <span style={{ fontSize: '0.85rem' }}>🏆</span>
            <span className="text-xs font-bold uppercase tracking-widest"
              style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>
              18è Premi Dona i Esport · BCN Esports
            </span>
          </motion.div>

          {/* Títol gran */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-black uppercase text-white mb-5 leading-none"
            style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(2.2rem, 5.5vw, 4.5rem)', letterSpacing: '0.04em' }}
          >
            Finalistes<br />
            <span style={{ color: 'var(--primary)' }}>Premi Dona</span><br />
            i Esport 25–26
          </motion.h2>

          {/* Badge Únic club */}
          <motion.div
            initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-5 w-fit"
            style={{ background: 'rgba(30,20,10,0.75)', border: '1px solid rgba(255,255,255,0.15)' }}
          >
            <span style={{ fontSize: '1.2rem' }}>🏀</span>
            <div>
              <p className="text-white font-bold text-sm leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Únic club de bàsquet
              </p>
              <p className="text-white/55 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                finalista de la convocatòria
              </p>
            </div>
          </motion.div>

          {/* Paràgraf */}
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.5 }}
            className="text-white/75 text-sm leading-relaxed mb-5 max-w-sm"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            El talent, la passió i el lideratge femení brillen al Clot. Molt contents d&apos;haver estat finalistes del Premi Dona i Esport lliurat al Saló de Cent de l&apos;Ajuntament de Barcelona.
          </motion.p>

          {/* Bullets */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.5 }}
            className="space-y-2 mb-6"
          >
            {[
              'Foment del bàsquet femení durant 60 anys',
              'Igualtat dins i fora de les pistes',
              'Reconeixement a tota la família Barna',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: 'var(--primary)' }} />
                <p className="text-white/80 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item}</p>
              </div>
            ))}
          </motion.div>

          {/* Link Instagram */}
          <motion.a
            href="https://www.instagram.com/p/DQr3YTDCO85/"
            target="_blank" rel="noopener noreferrer"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest"
            style={{ color: 'var(--primary)', fontFamily: 'Montserrat, sans-serif', textDecoration: 'none' }}
          >
            Ver en Instagram <span style={{ fontSize: '1.1rem' }}>›</span>
          </motion.a>
        </div>

        {/* ── DRETA: foto gran amb marc arrodonit ── */}
        <div className="flex-1 h-full flex items-center justify-center py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.93, x: 30 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="relative rounded-3xl overflow-hidden"
            style={{
              width: '100%',
              maxWidth: '420px',
              aspectRatio: '3/4',
              border: '2px solid rgba(227,30,36,0.35)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          >
            <div className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${IMAGES.ESCUDE_GROUP})` }} />
            {/* Overlay suau baix */}
            <div className="absolute bottom-0 left-0 right-0 h-16"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)' }} />
          </motion.div>
        </div>

      </div>
    </div>
  );
}

// ─── Slide components map ──────────────────────────────────────────

function SlidePresentation() {
  const stats = [
    { num: '+450', label: 'Famílies' },
    { num: '+40', label: 'Equips formació' },
    { num: '60', label: 'Anys' },
    { num: '65.5%', label: 'Staff tècnic dones' },
    { num: '38', label: 'Entrenadores' },
    { num: '+110', label: 'Equips 3×3 Barna' },
  ];

  return (
    <div className="relative w-full h-full flex overflow-hidden">
      {/* Fons fosc */}
      <div className="absolute inset-0" style={{ background: 'oklch(0.10 0.012 240)' }} />
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--primary)' }} />
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />
      <div className="absolute -right-32 -bottom-32 w-[500px] h-[500px] rounded-full opacity-[0.04]"
        style={{ background: 'var(--primary)' }} />

      <div className="relative z-10 w-full h-full flex items-center px-12 md:px-16 gap-12">

        {/* ── ESQUERRA: branding + botó ── */}
        <div className="flex flex-col justify-center items-start flex-shrink-0 w-[40%]">
          <motion.img
            src={IMAGES.LOGO_BARNA}
            alt="CB Grup Barna"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="h-20 w-auto mb-6"
            style={{ filter: 'drop-shadow(0 4px 16px rgba(227,30,36,0.3))' }}
          />

          <motion.p
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="uppercase tracking-widest text-xs font-semibold mb-3"
            style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
          >
            · Model Català · Mètode Barna
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="font-black uppercase text-white mb-4 leading-tight"
            style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(1.8rem, 4vw, 3.2rem)', letterSpacing: '0.06em' }}
          >
            CB Grup Barna<br />
            <span style={{ color: 'var(--primary)' }}>60 Anys</span><br />
            de Bàsquet i Valors
          </motion.h2>

          <motion.div
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            transition={{ delay: 0.45, duration: 0.5 }}
            className="h-px w-24 mb-5"
            style={{ background: 'var(--primary)', transformOrigin: 'left' }}
          />

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.5 }}
            className="text-white/55 text-sm leading-relaxed mb-7 max-w-xs"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Accedeix a la versió digital completa i interactiva de la nostra presentació institucional.
          </motion.p>

          {/* Botó */}
          <motion.a
            href="https://metode-barna.skywork.website"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="inline-flex items-center gap-3 px-6 py-3.5 rounded-2xl font-bold text-white uppercase tracking-widest text-sm cursor-pointer"
            style={{
              background: 'var(--primary)',
              textDecoration: 'none',
              fontFamily: 'Montserrat, sans-serif',
              boxShadow: '0 8px 32px rgba(227,30,36,0.4)',
              letterSpacing: '0.08em',
            }}
            whileHover={{ scale: 1.05 } as never}
            whileTap={{ scale: 0.97 } as never}
          >
            <span>🔗</span>
            <span>Veure Presentació</span>
            <span>↗</span>
          </motion.a>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
            className="mt-3 text-white/25 text-xs"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            metode-barna.skywork.website
          </motion.p>
        </div>

        {/* ── DRETA: grid d'estadístiques ── */}
        <div className="flex-1 flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="uppercase tracking-widest text-xs font-semibold mb-5"
            style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}
          >
            · En xifres
          </motion.p>

          <div className="grid grid-cols-3 gap-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.35 + i * 0.1, duration: 0.55 }}
                className="relative flex flex-col p-5 rounded-2xl"
                style={{
                  background: 'rgba(227,30,36,0.07)',
                  border: '1px solid rgba(227,30,36,0.22)',
                }}
              >
                <div
                  className="absolute top-0 left-4 right-4 h-px"
                  style={{ background: 'linear-gradient(90deg, transparent, var(--primary), transparent)' }}
                />
                <span
                  className="font-black leading-none mb-2"
                  style={{
                    fontFamily: 'Oswald, sans-serif',
                    fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                    color: 'var(--primary)',
                    textShadow: '0 0 30px rgba(227,30,36,0.3)',
                  }}
                >
                  {s.num}
                </span>
                <span
                  className="text-white/65 text-xs font-medium uppercase tracking-wide leading-tight"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {s.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

function SlideInstitutional() {
  const cards = [
    {
      img: IMAGES.INST_CONSELLER_1,
      title: 'Reunió amb el Conseller',
      text: "Bernat Álvarez, Conseller d'Esports de la Generalitat de Catalunya",
      url: 'https://www.instagram.com/p/DQmiLUECDno/',
    },
    {
      img: IMAGES.INST_ALCALDE_1,
      title: "Visita de l'Alcalde de Barcelona",
      text: "L'Alcalde de Barcelona visita el CB Grup Barna",
      url: 'https://www.instagram.com/p/DU7-my-CHKE/',
    },
    {
      img: IMAGES.INST_DAVID_1,
      title: 'David Escudé i VP Federació',
      text: "Presentació d'equips amb David Escudé i el Vicepresident de la Federació Catalana de Bàsquet",
      url: 'https://www.instagram.com/p/DVZSKc6CPCs/',
    },
  ];
  return (
    <div className="w-full h-full flex relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.TORRE_GLORIES})` }} />
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, rgba(8,8,8,0.92) 0%, rgba(8,8,8,0.72) 60%, rgba(8,8,8,0.55) 100%)'
      }} />
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />

      <div className="relative z-10 w-full h-full flex flex-col justify-center px-12 md:px-16 gap-0">
        <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="text-xs uppercase tracking-widest font-semibold mb-4"
          style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>
          · Recolzament Institucional
        </motion.p>
        <motion.h2 initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-3xl font-black uppercase leading-tight mb-6 text-white"
          style={{ fontFamily: 'Oswald, sans-serif' }}>
          Recolzament<br />Institucional
        </motion.h2>

        <div className="flex flex-col gap-3 w-full max-w-4xl">
          {cards.map((c, i) => (
            <motion.a key={c.title} href={c.url} target="_blank" rel="noopener noreferrer"
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.18, duration: 0.6 }}
              className="flex items-center gap-4 p-3 rounded-2xl relative overflow-hidden cursor-pointer group"
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                textDecoration: 'none',
              }}>
              <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'var(--primary)', opacity: 0.7 }} />
              <div className="flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden">
                <img src={c.img} alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </div>
              <div className="flex flex-col flex-1">
                <span className="text-white font-bold text-sm leading-tight mb-0.5"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}>{c.title}</span>
                <span className="text-xs leading-snug"
                  style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Montserrat, sans-serif' }}>{c.text}</span>
              </div>
              <div className="flex-shrink-0 text-xs font-bold" style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>↗</div>
            </motion.a>
          ))}
        </div>
      </div>
    </div>
  );
}

function SlideSponsors() {
  return (
    <div className="relative w-full h-full overflow-hidden">
      <div className="absolute inset-0" style={{ background: 'oklch(0.10 0.012 240)' }} />
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--primary)' }} />
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'var(--primary)' }} />
      <iframe
        src="/sponsors_embed.html"
        title="Col·laboradors & Patrocinadors"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          background: 'transparent',
        }}
        allowFullScreen
      />
    </div>
  );
}


const SLIDE_COMPONENTS = [
  SlideCover,
  SlideFamilies,
  SlideEquality,
  SlideDiversity,
  SlideCommunity,
  SlideProtection,
  SlideNeighborhood,
  SlideStreet,
  SlideAward,
  SlideInstitutional,
  SlidePresentation,
  SlideSponsors,
];

const SLIDE_LABELS = [
  'Portada',
  'Famílies',
  'Igualtat',
  'Diversitat',
  'Comunitat',
  'Protecció',
  'Barri',
  'Esport',
  'Premi',
  'Suport',
  'Presentació',
  'Patrocinadors',
];

// ─── Animation variants ───────────────────────────────────────────

function getVariants(direction: number) {
  return {
    enter: {
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    },
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: 'spring', stiffness: 220, damping: 28 },
        opacity: { duration: 0.4 },
      },
    },
    exit: {
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
      transition: {
        x: { type: 'spring', stiffness: 220, damping: 28 },
        opacity: { duration: 0.3 },
      },
    },
  };
}

// ─── Main Presentation ──────────────────────────────────────────────

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const touchStartX = useRef<number>(0);

  const goTo = useCallback((index: number) => {
    if (isAnimating || index === currentSlide) return;
    const clamped = Math.max(0, Math.min(TOTAL_SLIDES - 1, index));
    setDirection(clamped > currentSlide ? 1 : -1);
    setCurrentSlide(clamped);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 700);
  }, [currentSlide, isAnimating]);

  const prev = useCallback(() => goTo(currentSlide - 1), [currentSlide, goTo]);
  const next = useCallback(() => goTo(currentSlide + 1), [currentSlide, goTo]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  // Touch support
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0) next();
      else prev();
    }
  };

  const SlideComponent = SLIDE_COMPONENTS[currentSlide];
  const variants = getVariants(direction);

  return (
    <div
      className="relative w-screen h-screen overflow-hidden select-none"
      style={{ background: 'var(--background)' }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Slides */}
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={currentSlide}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0 w-full h-full"
        >
          <SlideComponent />
        </motion.div>
      </AnimatePresence>

      {/* Slide counter top-right */}
      <div
        className="absolute top-6 right-8 z-50 flex items-center gap-2"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        <span className="text-white/80 text-sm font-medium">
          {String(currentSlide + 1).padStart(2, '0')}
        </span>
        <span className="text-white/30 text-sm">/</span>
        <span className="text-white/30 text-sm">
          {String(TOTAL_SLIDES).padStart(2, '0')}
        </span>
      </div>

      {/* Club branding top-left — logo oficial */}
      <div className="absolute top-4 left-6 z-50">
        <img
          src={IMAGES.LOGO_BARNA}
          alt="CB Grup Barna"
          className="h-14 w-auto drop-shadow-lg"
          style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.7))' }}
        />
      </div>

      {/* Nav arrows */}
      {currentSlide > 0 && (
        <button
          onClick={prev}
          aria-label="Slide anterior"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <ChevronLeft className="w-5 h-5 text-white/70" />
        </button>
      )}
      {currentSlide < TOTAL_SLIDES - 1 && (
        <button
          onClick={next}
          aria-label="Slide següent"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <ChevronRight className="w-5 h-5 text-white/70" />
        </button>
      )}

      {/* Progress dots bottom */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5">
        {SLIDE_LABELS.map((label, i) => (
          <button
            key={label}
            onClick={() => goTo(i)}
            aria-label={`Anar a slide: ${label}`}
            title={label}
            className="relative group transition-all duration-300 focus:outline-none"
          >
            <div
              className="rounded-full transition-all duration-300"
              style={{
                width: i === currentSlide ? '24px' : '6px',
                height: '6px',
                background: i === currentSlide ? 'var(--primary)' : 'rgba(255,255,255,0.25)',
                boxShadow: i === currentSlide ? '0 0 8px rgba(227,30,36,0.6)' : 'none',
              }}
            />
            {/* Tooltip */}
            <span
              className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              style={{
                background: 'rgba(0,0,0,0.8)',
                color: 'rgba(255,255,255,0.85)',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              {label}
            </span>
          </button>
        ))}
      </div>

      {/* Bottom left: keyboard hint (only on first slide) */}
      {currentSlide === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-6 left-8 z-50 flex items-center gap-2"
        >
          <span
            className="text-white/25 text-xs"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            ← → Navegar
          </span>
        </motion.div>
      )}
    </div>
  );
}
